import { createContext, useCallback, useContext } from 'react';
import type { MutableRefObject } from 'react';
import type { TableEditableState } from '../types/tableEditableState';

export type SetEditableState = (
  state: TableEditableState | ((prev: TableEditableState) => TableEditableState)
) => void;

export type EditableContextValue = {
  editableState: TableEditableState;
  setEditableState: SetEditableState;
  /**
   * Always-current mirror of `editableState` for event handlers on memoized cells.
   * A sibling popup cell on the same row may skip re-render when another column
   * enters edit mode, so closures over `editableState` can be stale (DL-60610).
   */
  editableStateRef?: MutableRefObject<TableEditableState | null>;
  /**
   * Set true (synchronously) by the navigation guard while its "Leave without saving?"
   * dialog is open. Editing cells read this in their blur/click-away commit path and skip
   * committing/exiting — the dialog steals focus, which would otherwise blur the input and
   * save+close the cell before the user answers (DL-60097). A ref so it's visible the moment
   * the dialog grabs focus, without waiting for a re-render.
   */
  isNavBlockingRef?: MutableRefObject<boolean>;
  /**
   * When set, identifies a cell that has a pending validation error and is
   * blocking other cells from taking edit focus. Inline-edit cells must
   * consult this before entering edit mode and refuse if the lock points
   * elsewhere. Optional so existing providers keep working without it.
   */
  editLockedCell?: TableEditableState | null;
  setEditLockedCell?: (state: TableEditableState | null) => void;
};

export const EditableContext = createContext<EditableContextValue | null>(null);

/** Provider that only supplies editable context. Used by OptimizedTable outer so inner can stay memo. */
export const EditableOnlyProvider = ({
  value,
  children,
}: {
  value: EditableContextValue;
  children: React.ReactNode;
}) => <EditableContext.Provider value={value}>{children}</EditableContext.Provider>;

export function useEditableContext(): EditableContextValue {
  const ctx = useContext(EditableContext);
  if (ctx === null)
    throw new Error('useEditableContext must be used within an OptimizedTableProvider');
  return ctx;
}

/**
 * Optional editable context – returns null when not inside a provider.
 * Used by TableTooltip which may render outside the table.
 */
export function useEditableContextOptional(): EditableContextValue | null {
  return useContext(EditableContext);
}

/**
 * Predicate for inline-editing cells: returns true when the cell should skip its
 * blur / click-away commit because the leave-confirmation dialog is open.
 */
export function useShouldSkipInlineCommit(): () => boolean {
  const ctx = useContext(EditableContext);
  const navBlockingRef = ctx?.isNavBlockingRef;
  return useCallback(() => Boolean(navBlockingRef?.current), [navBlockingRef]);
}
